'use client';

import { ParsedUrlQuery } from 'querystring';

import { Identify, Types } from '@amplitude/analytics-browser';
import { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { Experiment, ExperimentClient, Variant } from '@amplitude/experiment-js-client';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useBreakPoint } from '../../../domains/Layout/BreakPoint/BreakPoint';
import { User } from '../../../resources/user/domain/types/User';
import { usePage } from '../../../shared/providers/page/usePage';
import { useUser } from '../../../shared/providers/user/useUser';
import { isBot } from '../../../shared/utilities/browser/isBot';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';
import { logger } from '../../Log/logger';

import { Ampli, ampli, ApiKey, Environment } from './__AUTO_GENERATED__';

export type TrackingProperties<F extends (properties: Parameters<F>[0]) => void> = Omit<
    Parameters<F>[0],
    keyof ReturnType<typeof getDefaultTrackingProperties>
>;

type AssignationData = Record<string, string>;

function getAmplitudeApiKey(): string | null {
    const env = process.env.NEXT_PUBLIC_AMPLITUDE_ENV;

    if (!env) {
        return null;
    }

    return ApiKey[env as Environment];
}

function getExperimentDeployKey(): string {
    const deployKey = process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOY_KEY;

    if (!deployKey) {
        throw new Error('Amplitude Experiment missing a deploy key');
    }

    return deployKey;
}

class SkipUserPropertiesSyncEnrichmentPlugin implements EnrichmentPlugin {
    name = 'skip-user-properties-sync-enrichment-plugin';
    type = 'enrichment' as const;

    async execute(event: Event): Promise<Event> {
        if (event.event_type === 'session_start') {
            // @ts-expect-error - Property '$skip_user_properties_sync' is not included in the Event type
            event.$skip_user_properties_sync = true;
        }
        return event;
    }
}

type AmplitudeContextValue = {
    isAmplitudeEnabled: boolean;
    experimentClient: ExperimentClient | null;
    client: Types.BrowserClient | null;
    experimentFetchVersion: number;
};

const AmplitudeContext = React.createContext<AmplitudeContextValue>({
    isAmplitudeEnabled: false,
    experimentClient: null,
    client: null,
    experimentFetchVersion: 0,
});

type UserForAmplitude = PickDomain<
    User,
    'accountId' | 'isEvaneos' | 'recommendationsIsEnabled' | 'numberOfUserQuotesByStateCounters',
    {
        status: User['status'] | null;
    }
>;

const identifiedUser = new Identify();
let isAmplitudeAlreadyLoaded = false;

export const AmplitudeProvider = (props: PropsWithChildren) => {
    const [hasConsented, setHasConsented] = useState(false);
    const [isAmplitudeLoaded, setIsAmplitudeLoaded] = useState(isAmplitudeAlreadyLoaded);
    const isAmplitudeClientCreated = Boolean(ampli.isLoaded);
    const isAmplitudeEnabled = hasConsented && isAmplitudeLoaded;
    const experimentClient = useRef<ExperimentClient | null>(null);
    const { isMobile } = useBreakPoint();
    const { seoContext } = usePage();
    const amplitudeApiKey = getAmplitudeApiKey();
    const { isUserConnectedOnLoad, isUserFetched, user } = useUser();

    // Incremented after each experiment fetch to re-trigger variant reads on SPA navigation and mid-session login
    const [experimentFetchVersion, setExperimentFetchVersion] = useState(0);

    // Unified experiment fetch: handles initial load, SPA navigation, and mid-session login.
    // Triggers when Amplitude is loaded, user changes (login), or page changes (SPA nav).
    useEffect(() => {
        if (!isAmplitudeLoaded || !experimentClient.current || shouldDisableExperiments()) return;
        if (isUserConnectedOnLoad && !isUserFetched) return;

        identifyUser(user);

        void experimentClient.current.fetch(buildExperimentUserProperties(seoContext)).then(() => {
            setExperimentFetchVersion((v) => v + 1);
        });
    }, [isAmplitudeLoaded, isUserConnectedOnLoad, isUserFetched, user, seoContext]);

    const contextProviderValue = useMemo(() => {
        return {
            isAmplitudeEnabled,
            client: isAmplitudeLoaded ? ampli.client : null,
            experimentClient: experimentClient.current,
            experimentFetchVersion,
        };
    }, [isAmplitudeEnabled, isAmplitudeLoaded, experimentFetchVersion]);

    if (amplitudeApiKey && !isAmplitudeClientCreated && !isAmplitudeLoaded && hasConsented) {
        ampli
            .load({
                client: {
                    apiKey: amplitudeApiKey,
                    configuration: {
                        serverZone: 'EU',
                        autocapture: {
                            pageViews: false,
                            sessions: true,
                            attribution: true,
                            fileDownloads: false,
                            formInteractions: false,
                        },
                    },
                },
            })
            .promise.then(() => {
                return ampli.client.add(new SkipUserPropertiesSyncEnrichmentPlugin()).promise;
            })
            .then(() => {
                return ampli.client.add(
                    sessionReplayPlugin({ sampleRate: sessionReplaySampleRate() }),
                ).promise;
            })
            .then(() => {
                identifiedUser.set('device_category', isMobile() ? 'mobile' : 'desktop');
                identifiedUser.set('isConnected', isUserConnectedOnLoad);
                return ampli.client.identify(identifiedUser);
            })
            .then(() => {
                experimentClient.current = Experiment.initializeWithAmplitudeAnalytics(
                    getExperimentDeployKey(),
                    {
                        serverUrl: 'https://api.lab.eu.amplitude.com',
                    },
                );
                window.amplitude = ampli.client;
                isAmplitudeAlreadyLoaded = true;
                setIsAmplitudeLoaded(true);
                logger.info('Amplitude loaded', {
                    experimentClient: Boolean(experimentClient.current),
                });
            })
            .catch((error) => {
                logger.error('Amplitude load failed', { error });
            });
    }

    useEffect(function listenTrackingConsent() {
        function onConsentChanged() {
            setHasConsented(Boolean(window.Didomi?.getUserConsentStatusForVendor('c:amplitude')));
        }
        window.didomiEventListeners = window.didomiEventListeners || [];
        window.didomiEventListeners.push({
            event: 'consent.changed',
            listener: onConsentChanged,
        });
        window.didomiOnReady = window.didomiOnReady || [];
        window.didomiOnReady.push(onConsentChanged);
    }, []);

    return (
        <AmplitudeContext.Provider value={contextProviderValue}>
            {props.children}
        </AmplitudeContext.Provider>
    );
};

function identifyUser(user: UserForAmplitude | null) {
    if (user) {
        ampli.client.setUserId(user.accountId);

        identifiedUser.set('isConnected', true);
        identifiedUser.set('is_evaneos', user.isEvaneos);
        identifiedUser.set('ab_reco_available', user.recommendationsIsEnabled);
        if (user.status) {
            identifiedUser.set('user_status', user.status);
        }

        if (user.numberOfUserQuotesByStateCounters) {
            identifiedUser.set('nb_request_done', user.numberOfUserQuotesByStateCounters.done);
            identifiedUser.set(
                'nb_request_confirmed',
                user.numberOfUserQuotesByStateCounters.confirmed,
            );
            identifiedUser.set(
                'nb_request_cancelled',
                user.numberOfUserQuotesByStateCounters.cancelled,
            );
            identifiedUser.set(
                'nb_request_in_progress',
                user.numberOfUserQuotesByStateCounters.in_progress,
            );
            identifiedUser.set(
                'nb_request_planning',
                user.numberOfUserQuotesByStateCounters.planning,
            );
        }
    }

    ampli.client.identify(identifiedUser);
}

function buildExperimentUserProperties(seoContext: string, extra?: Record<string, string>) {
    return {
        user_properties: {
            page: seoContext,
            site_name: getHostname(),
            ...extra,
        },
    };
}

function sessionReplaySampleRate(): number {
    const sampleRate = process.env.NEXT_PUBLIC_AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE ?? '0';
    return parseFloat(sampleRate);
}

function getCanonicalUrl() {
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
        return canonical.getAttribute('href');
    }

    return null;
}

const QUERY_PARAMS_TO_IGNORE = ['jwt', 'email'];

function removeIgnoredQueryParams(url: URL) {
    QUERY_PARAMS_TO_IGNORE.forEach((element) => {
        url.searchParams.delete(element);
    });
    return url;
}

function getPathnameFromUrl(urlToParse: string) {
    const url = document.createElement('a');
    url.href = urlToParse;

    return url.pathname;
}

function getReferrer() {
    return document.referrer || '$direct';
}

function getReferringDomain(referrer: string) {
    const split = referrer.split('/');
    if (split.length >= 3) {
        return split[2];
    }

    return '';
}

function getUrl(search: string) {
    const canonicalUrl = getCanonicalUrl();
    const url = canonicalUrl
        ? canonicalUrl.indexOf('?') > -1
            ? canonicalUrl
            : canonicalUrl + search
        : window.location.href;
    const hashIndex = url.indexOf('#');

    return hashIndex > -1 ? url.slice(0, hashIndex) : url;
}

function getHostname() {
    return window.location.hostname;
}

function getDefaultTrackingProperties(seoContext: string) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return {
            category: '',
            path: '',
            referrer: '',
            search: '',
            title: '',
            url: '',
            referring_domain: '',
            tab_url: '',
            site_name: '',
        };
    }

    const canonicalUrl = getCanonicalUrl();
    const sanitizedLocation = removeIgnoredQueryParams(new URL(window.location.href));
    const path = canonicalUrl ? getPathnameFromUrl(canonicalUrl) : sanitizedLocation.pathname;
    const { search } = sanitizedLocation;
    const { title } = document;
    const url = getUrl(search);
    const tabUrl = sanitizedLocation.href;
    const referrer = getReferrer();
    const referringDomain = getReferringDomain(referrer);
    const siteName = getHostname();

    return {
        category: seoContext,
        path,
        referrer,
        search,
        title,
        url,
        referring_domain: referringDomain,
        tab_url: tabUrl,
        site_name: siteName,
    };
}

const useAmplitude = () => {
    return useContext(AmplitudeContext);
};

/**
 * Get query params from URL (works in both App Router and Pages Router)
 * Only extracts experiment-related params for debug/testing
 */
function getExperimentQueryParams(): ParsedUrlQuery {
    if (typeof window === 'undefined') {
        return {};
    }

    const searchParams = new URLSearchParams(window.location.search);
    const query: ParsedUrlQuery = {};

    const withExperimentName = searchParams.get('withExperimentName');
    const withExperimentVariation = searchParams.get('withExperimentVariation');

    if (withExperimentName) {
        query.withExperimentName = withExperimentName;
    }
    if (withExperimentVariation) {
        query.withExperimentVariation = withExperimentVariation;
    }

    return query;
}

export const useLazyExperimentVariation = (experimentName: string): (() => Variant | undefined) => {
    const { experimentClient, experimentFetchVersion, isAmplitudeEnabled } = useAmplitude();
    const isExperimentFetched = experimentFetchVersion > 0;
    const { seoContext } = usePage();

    return useCallback(() => {
        if (!isExperimentFetched || !experimentClient || !isAmplitudeEnabled) return undefined;
        if (shouldDisableExperiments()) return undefined;

        const query = getExperimentQueryParams();
        if (query.withExperimentName === experimentName && query.withExperimentVariation) {
            return { value: query.withExperimentVariation as string };
        }

        const variant = experimentClient.variant(experimentName);
        if (variant.value) {
            // Update user properties and re-fetch localStorage assignation infos
            // -> Handle mutex with Type A user properties which are sync
            // (Type B are async and can take up to seconds to be effective)
            identifiedUser.set(experimentName, variant.value);
            ampli.client.identify(identifiedUser);
            void experimentClient.fetch(buildExperimentUserProperties(seoContext));
        }

        return variant;
    }, [isExperimentFetched, experimentClient, experimentName, seoContext, isAmplitudeEnabled]);
};

export const useExperimentVariation = (
    experimentName: string,
    assignationData?: AssignationData,
) => {
    const { experimentClient, experimentFetchVersion, isAmplitudeEnabled } = useAmplitude();
    const isExperimentFetched = experimentFetchVersion > 0;
    const { seoContext } = usePage();
    const [variant, setVariant] = useState<Variant>();
    const refAssignationData = useRef(assignationData);

    useEffect(() => {
        if (!isExperimentFetched || !experimentClient || !isAmplitudeEnabled) return;
        if (shouldDisableExperiments()) return;

        const query = getExperimentQueryParams();
        if (query.withExperimentName === experimentName && query.withExperimentVariation) {
            setVariant({ value: query.withExperimentVariation as string });
            return;
        }

        // No assignationData: the provider's global fetch already populated the cache
        if (!refAssignationData.current) {
            const v = experimentClient.variant(experimentName);
            if (v?.value) {
                setVariant(v);
            }
            return;
        }

        // With assignationData: the global fetch only sent page + site_name, but this experiment
        // targets on additional properties (e.g. destination_slug). Re-fetch to update the cache.
        void experimentClient
            .fetch(buildExperimentUserProperties(seoContext, refAssignationData.current))
            .then(() => {
                const v = experimentClient.variant(experimentName);
                if (v?.value) {
                    setVariant(v);
                }
            });
    }, [
        experimentFetchVersion, // Re-trigger variant reads on SPA navigation and mid-session login
        isExperimentFetched,
        experimentClient,
        experimentName,
        seoContext,
        isAmplitudeEnabled,
    ]);

    return variant;
};

function shouldDisableExperiments(): boolean {
    return isBot();
}

export const useTrackPageViewAmplitude = (
    callback: (
        ampli: Ampli,
        defaultProperties: ReturnType<typeof getDefaultTrackingProperties>,
    ) => void,
) => {
    const { isAmplitudeEnabled } = useAmplitude();
    const callbackRef = useRef(callback);
    const { seoContext } = usePage();

    useEffect(() => {
        if (isAmplitudeEnabled) {
            callbackRef.current(ampli, getDefaultTrackingProperties(seoContext));
        }
    }, [isAmplitudeEnabled, seoContext]);
};

type TrackEventType = (
    callback: (
        ampli: Ampli,
        defaultProperties: ReturnType<typeof getDefaultTrackingProperties>,
    ) => void,
) => void;

export const useTrackEventAmplitude = () => {
    const { isAmplitudeEnabled } = useAmplitude();
    const { seoContext } = usePage();

    const trackEvent: TrackEventType = useCallback(
        (callback) => {
            if (isAmplitudeEnabled) {
                callback(ampli, getDefaultTrackingProperties(seoContext));
            }
        },
        [isAmplitudeEnabled, seoContext],
    );

    return {
        trackEvent,
    };
};
